import React from "react";
import colorsStore from "shared/store/colorsStore";
import {
  formatNumber,
  formatNumber3,
  formatPercentage,
  formatPercentageNo100,
} from "utils/formatUtils";
import { riskProfileMapEngToIta } from "utils/misc";
import styles from "./caratteristicheDiPortafoglioCompare.module.scss";
import { useTranslation } from "react-i18next";

type SectionChildrenProps = {
  data: any;
  data2: any;
};

const CaratteristicaDiPortafoglioCompare: React.FC<SectionChildrenProps> = ({
  data,
  data2,
}) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  if (!data || !data2) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.gridContainer}
        style={{ color: colorsConfig.finecoBlack }}
      >
        {/* First Column */}
        <div className={styles.columnContainer}>
          <div className={styles.portfolioHeader}>
            <div></div>
            <div>{t("portafoglioCompare.portafoglio1")}</div>
            <div>{t("portafoglioCompare.portafoglio2")}</div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.rendimentoAtteso")}
            </div>
            <div className={styles.valueCell}>
              {formatPercentage(data?.return)}
            </div>
            <div className={styles.valueCell}>
              {formatPercentage(data2?.return)}
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>{t("portfolio.var")}</div>
            <div className={styles.valueCell}>
              {formatPercentage(data?.var)}
            </div>
            <div className={styles.valueCell}>
              {formatPercentage(data2?.var)}
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>{t("portfolio.sharpe")}</div>
            <div className={styles.valueCell}>
              {formatNumber3(data?.sharpe)}
            </div>
            <div className={styles.valueCell}>
              {formatNumber3(data2?.sharpe)}
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.pianoFinanziario")}
            </div>
            <div className={styles.valueCell}>{data?.financial_plan}</div>
            <div className={styles.valueCell}>{data2?.financial_plan}</div>
          </div>
        </div>

        {/* Second Column */}
        <div className={styles.columnContainer}>
          <div className={styles.portfolioHeader}>
            <div></div>
            <div>{t("portafoglioCompare.portafoglio1")}</div>
            <div>{t("portafoglioCompare.portafoglio2")}</div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.dividendYield1y")}
            </div>
            <div className={styles.valueCell}>
              {formatPercentage(data?.dividend_yield)}
            </div>
            <div className={styles.valueCell}>
              {formatPercentage(data2?.dividend_yield)}
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>{t("portfolio.ytm")}</div>
            <div className={styles.valueCell}>
              {formatPercentageNo100(data?.ytm)}
            </div>
            <div className={styles.valueCell}>
              {formatPercentageNo100(data2?.ytm)}
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>{t("portfolio.duration")}</div>
            <div className={styles.valueCell}>
              {formatNumber(data?.duration)} anni
            </div>
            <div className={styles.valueCell}>
              {formatNumber(data2?.duration)} anni
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.costiStrumenti")}
            </div>
            <div className={styles.valueCell}>
              {formatPercentage(data?.total_cost)}
            </div>
            <div className={styles.valueCell}>
              {formatPercentage(data2?.total_cost)}
            </div>
          </div>
        </div>

        {/* Third Column */}
        <div className={styles.columnContainer}>
          <div className={styles.portfolioHeader}>
            <div></div>
            <div>{t("portafoglioCompare.portafoglio1")}</div>
            <div>{t("portafoglioCompare.portafoglio2")}</div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.ratingEsgFineco")}
            </div>
            <div className={styles.valueCell}>{formatNumber(data?.esg)}</div>
            <div className={styles.valueCell}>{formatNumber(data2?.esg)}</div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.finalScore")}
            </div>
            <div className={styles.valueCell}>
              {formatNumber(data?.final_score)}
            </div>
            <div className={styles.valueCell}>
              {formatNumber(data2?.final_score)}
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.finalScoreAdvice")}
            </div>
            <div className={styles.valueCell}>
              {formatNumber(data?.final_score_advice)}
            </div>
            <div className={styles.valueCell}>
              {formatNumber(data2?.final_score_advice)}
            </div>
          </div>

          <div className={styles.comparisonRow}>
            <div className={styles.featureTitle}>
              {t("portfolio.profiloDiRischio")}
            </div>
            <div className={styles.valueCell}>
              {riskProfileMapEngToIta(data?.risk_profile)}
            </div>
            <div className={styles.valueCell}>
              {riskProfileMapEngToIta(data2?.risk_profile)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(
  CaratteristicaDiPortafoglioCompare,
  (prevProps: SectionChildrenProps, nextProps: SectionChildrenProps) => {
    return (
      prevProps.data === nextProps.data && prevProps.data2 === nextProps.data2
    );
  },
);
